/**
 * Counting progress, unlock, and resume selectors.
 * Mirrors iOS CountingLadderProgressStore + CountingWorldCatalog + map prefix gating.
 *
 * Course key is exactly `counting`. Completion IDs are `counting-XX`.
 * Lesson prerequisites are NOT sequential. World cards unlock by prior WORLD
 * completion. Inside a world, map nodes unlock by that world's lesson prefix.
 */

import {
  COUNTING_ACTIVITY_IDS,
  COUNTING_COURSE_KEY,
  COUNTING_LESSONS,
  COUNTING_PREREQUISITES,
  countingLessonByNumber,
  countingLessonContaining,
} from "../data/countingLadderCatalog";

export const COUNTING_MAP_ASPECT = 1024 / 2208;

const THREE_STOP_TRAIL = [
  { x: 0.31, y: 0.77 },
  { x: 0.68, y: 0.55 },
  { x: 0.39, y: 0.32 },
];

const FOUR_STOP_TRAIL = [
  { x: 0.31, y: 0.79 },
  { x: 0.68, y: 0.65 },
  { x: 0.39, y: 0.52 },
  { x: 0.68, y: 0.39 },
];

export const COUNTING_WORLDS = [
  {
    id: "meadow",
    title: "Meadow World",
    lessonNumbers: [1, 2, 3],
    overviewArt: "counting_meadow_world_overview",
    trailArt: "counting_meadow_world",
    nodes: [
      { x: 0.31, y: 0.77 },
      { x: 0.68, y: 0.55 },
      { x: 0.39, y: 0.4 },
    ],
    theme: {
      accent: "#40C27D",
      accentDeep: "#0A6E3D",
      support: "#FFC233",
      ink: "#0F2140",
      cream: "#FFFAE8",
      cardTop: "#F0FFD9",
      cardBottom: "#D6F4B8",
    },
  },
  {
    id: "number-garden",
    title: "Number Garden",
    lessonNumbers: [4, 6, 7, 8],
    overviewArt: "counting_number_garden_overview",
    trailArt: "counting_number_garden",
    nodes: FOUR_STOP_TRAIL,
    theme: {
      accent: "#7A6EED",
      accentDeep: "#4A339E",
      support: "#FFC440",
      ink: "#0F2140",
      cream: "#FFFAE8",
      cardTop: "#F5EBFF",
      cardBottom: "#DEC9FA",
    },
  },
  {
    id: "ten-town",
    title: "Ten Town",
    lessonNumbers: [9, 11, 13],
    overviewArt: "counting_ten_town_overview",
    trailArt: "counting_ten_town",
    nodes: THREE_STOP_TRAIL,
    theme: {
      accent: "#FA7D47",
      accentDeep: "#9E332E",
      support: "#FFCC45",
      ink: "#0F2140",
      cream: "#FFFAE8",
      cardTop: "#E8FAFF",
      cardBottom: "#C7EDFF",
    },
  },
];

export function countingWorldById(id) {
  return COUNTING_WORLDS.find((w) => w.id === id) || null;
}

export function countingWorldContainingLesson(lessonNumber) {
  return (
    COUNTING_WORLDS.find((w) => w.lessonNumbers.includes(Number(lessonNumber))) ||
    null
  );
}

export function completedCountingIds(courses) {
  const raw = courses?.[COUNTING_COURSE_KEY];
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const ids = [];
  for (const id of raw) {
    if (typeof id !== "string" || !COUNTING_ACTIVITY_IDS.has(id) || seen.has(id)) {
      continue;
    }
    seen.add(id);
    ids.push(id);
  }
  return ids.sort();
}

export function completedCountingSet(courses) {
  return new Set(completedCountingIds(courses));
}

export function unionCountingIds(existing, incoming) {
  return completedCountingIds({
    [COUNTING_COURSE_KEY]: [...(existing || []), ...(incoming || [])],
  });
}

export function isCountingLessonComplete(lesson, completed) {
  const resolved =
    typeof lesson === "number" ? countingLessonByNumber(lesson) : lesson;
  if (!resolved) return false;
  const set = completed instanceof Set ? completed : new Set(completed || []);
  return resolved.activities.every((a) => set.has(a.id));
}

export function isCountingLessonUnlocked(lesson, completed) {
  const resolved =
    typeof lesson === "number" ? countingLessonByNumber(lesson) : lesson;
  if (!resolved) return false;
  const required = COUNTING_PREREQUISITES[resolved.number];
  if (!required) return false;
  const set = completed instanceof Set ? completed : new Set(completed || []);
  return required.every((number) =>
    isCountingLessonComplete(countingLessonByNumber(number), set),
  );
}

export function firstUnfinishedActivity(lesson, completed) {
  const resolved =
    typeof lesson === "number" ? countingLessonByNumber(lesson) : lesson;
  if (!resolved?.activities?.length) return null;
  const set = completed instanceof Set ? completed : new Set(completed || []);
  return resolved.activities.find((a) => !set.has(a.id)) || resolved.activities[0];
}

export function nextCountingLesson(completed) {
  const set = completed instanceof Set ? completed : new Set(completed || []);
  return (
    COUNTING_LESSONS.find(
      (lesson) =>
        isCountingLessonUnlocked(lesson, set) &&
        !isCountingLessonComplete(lesson, set),
    ) || null
  );
}

export function isCountingWorldComplete(world, completed) {
  const resolved = typeof world === "string" ? countingWorldById(world) : world;
  if (!resolved) return false;
  return resolved.lessonNumbers.every((number) =>
    isCountingLessonComplete(number, completed),
  );
}

export function isCountingWorldUnlocked(world, completed) {
  const resolved = typeof world === "string" ? countingWorldById(world) : world;
  if (!resolved) return false;
  const index = COUNTING_WORLDS.findIndex((w) => w.id === resolved.id);
  if (index <= 0) return true;
  return isCountingWorldComplete(COUNTING_WORLDS[index - 1], completed);
}

export function completedWorldPrefixCount(world, completed) {
  const resolved = typeof world === "string" ? countingWorldById(world) : world;
  if (!resolved) return 0;
  let count = 0;
  for (const number of resolved.lessonNumbers) {
    if (!isCountingLessonComplete(number, completed)) break;
    count += 1;
  }
  return count;
}

export function countingLessonChipState(world, lessonIndex, completed) {
  const resolved = typeof world === "string" ? countingWorldById(world) : world;
  if (!resolved) return "locked";
  const number = resolved.lessonNumbers[lessonIndex];
  if (number == null) return "locked";
  if (isCountingLessonComplete(number, completed)) return "complete";
  const unlocked = isCountingWorldUnlocked(resolved, completed);
  const firstIncomplete = resolved.lessonNumbers.findIndex(
    (n) => !isCountingLessonComplete(n, completed),
  );
  if (unlocked && lessonIndex === firstIncomplete) return "current";
  return "locked";
}

export function countingMapNodeState(world, lessonIndex, completed) {
  const resolved = typeof world === "string" ? countingWorldById(world) : world;
  if (!resolved) return "locked";
  const prefix = completedWorldPrefixCount(resolved, completed);
  if (lessonIndex < prefix) return "complete";
  if (lessonIndex === prefix) return "current";
  return "locked";
}

export function canOpenCountingLesson(lessonNumber, completed) {
  const lesson = countingLessonByNumber(lessonNumber);
  const world = countingWorldContainingLesson(lessonNumber);
  if (!lesson || !world) return false;
  if (!isCountingWorldUnlocked(world, completed)) return false;
  if (!isCountingLessonUnlocked(lesson, completed)) return false;
  const index = world.lessonNumbers.indexOf(lesson.number);
  return index >= 0 && index <= completedWorldPrefixCount(world, completed);
}

export function countingActivityRoute(lessonNumber, activityId) {
  return `/learn/counting/${lessonNumber}/${activityId}`;
}

export function countingWorldRoute(worldId) {
  return `/learn/counting/world/${worldId}`;
}

/**
 * Resolve a player deep-link. Locked or unknown routes redirect safely.
 */
export function resolveCountingPlayerRoute({
  lessonNumber,
  activityId,
  completed,
}) {
  const lesson = countingLessonByNumber(lessonNumber);
  if (!lesson) {
    return { redirectTo: "/learn/counting" };
  }
  if (!canOpenCountingLesson(lesson.number, completed)) {
    const world = countingWorldContainingLesson(lesson.number);
    if (world && isCountingWorldUnlocked(world, completed)) {
      return { redirectTo: countingWorldRoute(world.id) };
    }
    const next = nextCountingLesson(completed);
    const nextWorld = next
      ? countingWorldContainingLesson(next.number)
      : COUNTING_WORLDS[0];
    return { redirectTo: countingWorldRoute(nextWorld?.id || "meadow") };
  }

  const resume = firstUnfinishedActivity(lesson, completed);
  const known = lesson.activities.some((a) => a.id === activityId);
  if (!known) {
    return {
      redirectTo: countingActivityRoute(lesson.number, resume.id),
    };
  }
  return {
    lesson,
    activity: lesson.activities.find((a) => a.id === activityId),
    redirectTo: null,
  };
}

export function completedCountingWorlds(completed) {
  return COUNTING_WORLDS.filter((world) =>
    isCountingWorldComplete(world, completed),
  ).length;
}

export function isCountingCourseComplete(completed) {
  return COUNTING_LESSONS.every((lesson) =>
    isCountingLessonComplete(lesson, completed),
  );
}

export function selectLearnCountingProgress(courses) {
  const completed = completedCountingSet(courses);
  const nextLesson = nextCountingLesson(completed);
  const nextWorld = nextLesson
    ? countingWorldContainingLesson(nextLesson.number)
    : COUNTING_WORLDS[COUNTING_WORLDS.length - 1];
  const resumeActivity = nextLesson
    ? firstUnfinishedActivity(nextLesson, completed)
    : null;

  return {
    countingIds: completedCountingIds(courses),
    countingSet: completed,
    nextLesson,
    nextWorld,
    continueWorld:
      nextWorld && isCountingWorldUnlocked(nextWorld, completed)
        ? nextWorld
        : COUNTING_WORLDS.find((w) => isCountingWorldUnlocked(w, completed)) ||
          COUNTING_WORLDS[0],
    resumeActivity,
    completedWorlds: completedCountingWorlds(completed),
    countingFinished: isCountingCourseComplete(completed),
    countingDone: COUNTING_LESSONS.filter((lesson) =>
      isCountingLessonComplete(lesson, completed),
    ).length,
  };
}

export function countingLessonByActivityId(activityId) {
  return countingLessonContaining(activityId);
}
